import type { Command } from '../../types/commands'
import { removeSessionNode, findSessionNode } from '../sessionFilesystem'
import { parseCommandArgs, hasFlag } from '../argParser'
import { getContextDefaults } from '../../utils/contextUtils'

export const rmCommand: Command = {
  name: 'rm',
  description: 'Remove files or directories',
  usage: 'rm [-r] [-f] <name>',
  category: 'system',
  acceptsFiles: true,
  acceptsDirectories: true,
  execute: (args, context) => {
    if (args.length === 0) {
      return [
        {
          type: 'error',
          content: 'rm: missing operand\nUsage: rm [-r] [-f] <name>\n\nFlags:\n  -r  Remove directories recursively\n  -f  Force removal without confirmation',
        },
      ]
    }

    // Parse command arguments
    const parsed = parseCommandArgs(args)
    const hasRecursiveFlag = hasFlag(parsed, 'r', 'recursive')
    const hasForceFlag = hasFlag(parsed, 'f', 'force')
    const targetName = parsed.positional[0]

    if (!targetName) {
      return [
        {
          type: 'error',
          content: 'rm: missing operand\nUsage: rm [-r] <name>',
        },
      ]
    }

    // Get context with defaults
    const { currentDirectory, sessionFs } = getContextDefaults(context)

    // Find the node
    const fullPath = currentDirectory === '~' ? targetName : `${currentDirectory}/${targetName}`
    const node = findSessionNode(sessionFs, fullPath)

    if (!node) {
      return [
        {
          type: 'error',
          content: `rm: cannot remove '${targetName}': No such file or directory`,
        },
      ]
    }

    // Check if it's a directory
    if (node.type === 'directory') {
      if (!hasRecursiveFlag) {
        return [
          {
            type: 'error',
            content: `rm: cannot remove '${targetName}': Is a directory\n\nUse 'rm -r ${targetName}' to remove empty directories\nUse 'rm -rf ${targetName}' to force remove directories with contents`,
          },
        ]
      }

      // Check if directory has contents and requires force flag
      if (node.children && node.children.length > 0 && !hasForceFlag) {
        const childCount = node.children.length
        const childList = node.children.map(c => `  • ${c.name}`).join('\n')
        return [
          {
            type: 'error',
            content: `rm: cannot remove '${targetName}': Directory not empty\n\nContains ${childCount} ${childCount === 1 ? 'item' : 'items'}:\n${childList}\n\n⚠️  To confirm deletion, use: rm -rf ${targetName}`,
          },
        ]
      }
    }

    // Remove the node
    const newFs = removeSessionNode(sessionFs, currentDirectory, targetName)
    context?.setSessionFilesystem(newFs)

    return [
      {
        type: 'success',
        content: `Removed: ${targetName}`,
      },
    ]
  },
}
